import { BehaviorSubject, Observable } from 'rxjs';
import { BackendApiResponse, DateString, DateTimeString, FromTo, IntroLessonFormats } from '@shared/core/types';
import { TooltipPosition } from '@angular/material/tooltip';

export class FormField {
  key: string;
  type: FieldType;
  name: string;
  value?: TypeFormFieldValue;
  validators?: FieldValidators;
  options?: FormFieldOptions;
  groupedOptions?: GroupedFormFieldOptionsList;
  autocomplete?: AutocompleteFn;
  autocompleteWithOptions?: boolean;
  autocompleteTooltipConfig?: AutocompleteTooltipConfig;
  serializeTo?: 'value' | 'label';
  disabled?: boolean;
  showClearButton?: boolean;
  multiple?: boolean;
  dateTimeFormat?: DateTimeString;
  dateFormat?: DateString;
  readonly?: boolean;
  disableToggle?: boolean; // todo refactor this
  restrictEmitEventOnToggleDisable?: boolean;
  hideCalendarSelectedDates?: boolean; // only for date-interval/datetime/datetime-interval components

  constructor(properties: FormField) {
    Object.assign(this, properties);
  }
}

export enum FieldType {
  autocomplete = 'autocomplete',
  checkbox = 'checkbox',
  date = 'date',
  dateInterval = 'date-interval',
  datetime = 'datetime',
  datetimeInterval = 'datetime-interval',
  file = 'file',
  number = 'number',
  overbookingGrid = 'overbooking-grid',
  schedule = 'schedule',
  select = 'select',
  text = 'text',
  textarea = 'textarea',
  radioButton = 'radio-button',
  wysiwyg = 'wysiwyg',
  checkboxSlider = 'checkbox-slider',
  searchLocal = 'searchLocal',
}

export interface FieldValidators {
  required?: boolean;
  min?: number | DateTimeString | Date;
  max?: number | DateTimeString | Date;
  json?: boolean;
  integer?: boolean;
}

export interface FormFieldOption {
  value: string | number | boolean;
  label: string;
  title?: string;
}

export interface GroupedFormFieldOptions {
  label: string;
  options: FormFieldOption[];
}

export interface ScheduleInputValue {
  day: number;
  time: number;
  minutes: number;
  priority: ScheduleInputValuePriority;
}

export type ScheduleInputValuePriority = 'normal' | 'low';

export interface OverbookingGridSlot {
  date: DateString;
  time: OverbookingGridSlotTime[];
}

export interface OverbookingGridSlotTime {
  startDatetime: DateTimeString;
  slot: string;
  duration: number;
  isAvailable: boolean;
  isBooked: boolean;
}

export interface SlotReserve {
  startDatetime: DateTimeString;
  format: IntroLessonFormats | null;
}

export type FormFieldOptions = FormFieldOption[] | Observable<FormFieldOption[]> | BehaviorSubject<FormFieldOption[]>;
export type GroupedFormFieldOptionsList =
  | GroupedFormFieldOptions[]
  | Observable<GroupedFormFieldOptions[]>
  | BehaviorSubject<GroupedFormFieldOptions[]>;
export type FormFieldOptionsResponse = BackendApiResponse<FormFieldOption[]>;
export type AutocompleteOptions = FormFieldOption[] | string[];
export type AutocompleteResponse = BackendApiResponse<AutocompleteOptions>;
export type AutocompleteFn = (text: string) => Observable<FormFieldOption[] | string[]>;

export interface FormControlOptions {
  dateTimeFormat?: string;
  dateFormat?: string;
}

export interface DateInterval {
  to: string;
  from: string;
}

export interface AutocompleteTooltipConfig {
  isVisible: boolean;
  position: TooltipPosition;
}

export type FormFieldSerializedValue = null | string | number | boolean | any[] | FromTo<DateString> | FileList;
export type FormFieldSerializedValues = Record<string, FormFieldSerializedValue>;
export type TypeFormFieldValue =
  | string
  | number
  | boolean
  | Date
  | FormFieldOption
  | FromTo<DateString>
  | FromTo<DateTimeString>
  | (string | number | FormFieldOption | ScheduleInputValue)[];
